import React from 'react';
import { Link } from 'react-router-dom';

const s = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#070b14',
    fontFamily: "'Inter', -apple-system, sans-serif",
    color: '#e8eef8',
  },
  nav: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 48px',
    borderBottom: '1px solid #1a2640',
  },
  logo: {
    fontSize: '11px',
    letterSpacing: '0.3em',
    fontWeight: 700,
    color: '#4f8ef7',
    textDecoration: 'none',
  },
  navLink: {
    fontSize: '12px',
    letterSpacing: '0.15em',
    color: '#8099b8',
    textDecoration: 'none',
    marginLeft: '32px',
  },
  hero: {
    maxWidth: '820px',
    margin: '0 auto',
    padding: '80px 48px 48px',
  },
  eyebrow: {
    fontSize: '11px',
    letterSpacing: '0.3em',
    color: '#4f8ef7',
    textTransform: 'uppercase',
    marginBottom: '20px',
    fontWeight: 600,
  },
  headline: {
    fontSize: '2.6rem',
    fontWeight: 800,
    lineHeight: 1.15,
    color: '#ffffff',
    marginBottom: '24px',
    letterSpacing: '-0.02em',
  },
  subhead: {
    fontSize: '1.1rem',
    color: '#8099b8',
    lineHeight: 1.7,
    marginBottom: '0',
    maxWidth: '640px',
  },
  divider: {
    border: 'none',
    borderTop: '1px solid #1a2640',
    margin: '0 48px',
  },
  content: {
    maxWidth: '820px',
    margin: '0 auto',
    padding: '0 48px 80px',
  },
  section: {
    marginTop: '56px',
  },
  sectionLabel: {
    fontSize: '10px',
    letterSpacing: '0.3em',
    color: '#4f8ef7',
    textTransform: 'uppercase',
    fontWeight: 700,
    marginBottom: '16px',
  },
  sectionTitle: {
    fontSize: '1.4rem',
    fontWeight: 700,
    color: '#ffffff',
    marginBottom: '16px',
  },
  body: {
    fontSize: '1rem',
    color: '#a8bdd6',
    lineHeight: 1.8,
    marginBottom: '16px',
  },
  strong: {
    color: '#e8eef8',
    fontWeight: 600,
  },
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '20px',
    marginTop: '24px',
  },
  card: {
    background: 'linear-gradient(135deg, #0d1b2e 0%, #0a1520 100%)',
    border: '1px solid #1a2e48',
    borderRadius: '12px',
    padding: '28px 24px',
  },
  cardIcon: {
    fontSize: '22px',
    marginBottom: '12px',
  },
  cardTitle: {
    fontSize: '0.85rem',
    fontWeight: 700,
    color: '#ffffff',
    letterSpacing: '0.05em',
    marginBottom: '8px',
  },
  cardBody: {
    fontSize: '0.85rem',
    color: '#7a96b5',
    lineHeight: 1.65,
  },
  statRow: {
    display: 'flex',
    gap: '0',
    marginTop: '28px',
    borderRadius: '12px',
    overflow: 'hidden',
    border: '1px solid #1a2e48',
  },
  stat: {
    flex: 1,
    padding: '24px',
    textAlign: 'center',
    borderRight: '1px solid #1a2e48',
    background: '#0a1520',
  },
  statLast: {
    flex: 1,
    padding: '24px',
    textAlign: 'center',
    background: '#0a1520',
  },
  statNumber: {
    fontSize: '2rem',
    fontWeight: 800,
    color: '#4f8ef7',
    lineHeight: 1,
    marginBottom: '6px',
  },
  statLabel: {
    fontSize: '0.78rem',
    color: '#5a7a9a',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
  },
  highlightBox: {
    background: 'linear-gradient(135deg, #0d1f38 0%, #091628 100%)',
    border: '1px solid #1e3a5f',
    borderLeft: '3px solid #4f8ef7',
    borderRadius: '10px',
    padding: '24px 28px',
    marginTop: '24px',
  },
  highlightText: {
    fontSize: '1.05rem',
    color: '#c8d8ee',
    lineHeight: 1.75,
    fontStyle: 'italic',
    margin: 0,
  },
  cta: {
    marginTop: '64px',
    padding: '40px',
    background: 'linear-gradient(135deg, #0d1f38 0%, #091628 100%)',
    border: '1px solid #1a2e48',
    borderRadius: '14px',
    textAlign: 'center',
  },
  ctaTitle: {
    fontSize: '1.4rem',
    fontWeight: 700,
    color: '#ffffff',
    marginBottom: '12px',
  },
  ctaBody: {
    fontSize: '0.95rem',
    color: '#8099b8',
    marginBottom: '28px',
    lineHeight: 1.7,
  },
  ctaBtn: {
    display: 'inline-block',
    background: '#4f8ef7',
    color: '#ffffff',
    padding: '14px 36px',
    borderRadius: '8px',
    fontWeight: 700,
    fontSize: '0.9rem',
    letterSpacing: '0.05em',
    textDecoration: 'none',
    border: 'none',
    cursor: 'pointer',
  },
  footer: {
    borderTop: '1px solid #1a2640',
    padding: '28px 48px',
    textAlign: 'center',
    fontSize: '11px',
    color: '#3a5070',
    letterSpacing: '0.1em',
  },
};

const capabilities = [
  {
    icon: '⚡',
    title: 'AI-Native Infrastructure',
    body: 'Every system we build runs on AI at the core — not as a bolt-on feature. That means faster delivery, smarter automation, and platforms that improve over time.',
  },
  {
    icon: '🛒',
    title: 'E-Commerce & Retail Systems',
    body: 'Inventory management, online storefronts, POS integration, and real-time product listings. We turn physical inventory into digital revenue.',
  },
  {
    icon: '🔧',
    title: 'Operations Platforms',
    body: 'Staff portals, service management, HR tools, role-based access, and reporting dashboards — built to scale without adding headcount.',
  },
  {
    icon: '📡',
    title: 'Data Pipelines & Market Intelligence',
    body: 'Custom scraping, market analysis, competitive pricing, and automated reporting. We make your data work while you sleep.',
  },
  {
    icon: '🌐',
    title: 'Web Design & Digital Presence',
    body: 'Professional, mobile-ready websites with real backends. Not templates — purpose-built for your business and your customers.',
  },
  {
    icon: '🤖',
    title: 'Custom AI Tools',
    body: 'Trained AI assistants, automated workflows, document generation, and intelligent scheduling — tools that do the work your staff doesn\'t have time for.',
  },
];

export default function AboutPage() {
  return (
    <div style={s.page}>

      {/* Nav */}
      <nav style={s.nav}>
        <Link to="/" style={s.logo}>RAMDESIGNWORKS</Link>
        <div>
          <Link to="/start-a-project" style={s.navLink}>START A PROJECT</Link>
          <a href="mailto:contact@ramdesignworks.com" style={s.navLink}>CONTACT</a>
          <Link to="/" style={s.navLink}>HOME</Link>
        </div>
      </nav>

      {/* Hero */}
      <div style={s.hero}>
        <p style={s.eyebrow}>About RAMDESIGNWORKS</p>
        <h1 style={s.headline}>Agency capacity.<br />Without the agency price tag.</h1>
        <p style={s.subhead}>
          RAMDESIGNWORKS is a professional digital services firm operating at the intersection of
          AI and practical business operations. We build the platforms, pipelines, and systems
          that let businesses grow without hiring a department to run them.
        </p>
      </div>

      <hr style={s.divider} />

      <div style={s.content}>

        {/* What makes us different */}
        <div style={s.section}>
          <p style={s.sectionLabel}>The Difference</p>
          <h2 style={s.sectionTitle}>Built AI-native from the ground up</h2>
          <p style={s.body}>
            Most agencies adopt AI as a productivity add-on. RAMDESIGNWORKS was built around it.
            Every workflow, every deliverable, every system we design runs on AI-native infrastructure
            from day one. That means <span style={s.strong}>faster builds, leaner operations, and
            platforms that cost a fraction of what a traditional dev shop would charge</span> — without
            cutting corners on capability or quality.
          </p>
          <p style={s.body}>
            The result for clients: enterprise-grade digital systems at a price point designed for
            independent businesses, marine dealers, specialty retailers, and service operators who
            have been priced out of that level of capability until now.
          </p>

          <div style={s.highlightBox}>
            <p style={s.highlightText}>
              "What takes a traditional 5-person agency six months and $80,000 to deliver,
              we build in weeks — and hand you the keys with a license structure designed
              to grow with your revenue, not drain it."
            </p>
          </div>
        </div>

        {/* Stats */}
        <div style={s.section}>
          <p style={s.sectionLabel}>Operational Scale</p>
          <h2 style={s.sectionTitle}>What we run right now</h2>
          <p style={s.body}>
            RAMDESIGNWORKS is not a startup with a pitch deck. We are an active, operating firm
            with live production systems running across multiple industries and platforms.
          </p>
          <div style={s.statRow}>
            <div style={s.stat}>
              <div style={s.statNumber}>5+</div>
              <div style={s.statLabel}>Live Production Platforms</div>
            </div>
            <div style={s.stat}>
              <div style={s.statNumber}>Cloud</div>
              <div style={s.statLabel}>Firebase / GCP Infrastructure</div>
            </div>
            <div style={s.stat}>
              <div style={s.statNumber}>AI</div>
              <div style={s.statLabel}>At Every Layer</div>
            </div>
            <div style={s.statLast}>
              <div style={s.statNumber}>Weeks</div>
              <div style={s.statLabel}>Typical Delivery Window</div>
            </div>
          </div>
        </div>

        {/* Capabilities */}
        <div style={s.section}>
          <p style={s.sectionLabel}>What We Build</p>
          <h2 style={s.sectionTitle}>Core capabilities</h2>
          <p style={s.body}>
            We work across a focused set of disciplines — deeply, not broadly.
            Every service listed below is one we have shipped in production.
          </p>
          <div style={s.cardGrid}>
            {capabilities.map((c, i) => (
              <div key={i} style={s.card}>
                <div style={s.cardIcon}>{c.icon}</div>
                <div style={s.cardTitle}>{c.title}</div>
                <div style={s.cardBody}>{c.body}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Bandwidth & Affordability */}
        <div style={s.section}>
          <p style={s.sectionLabel}>Bandwidth & Affordability</p>
          <h2 style={s.sectionTitle}>Full capacity. No retainer bloat.</h2>
          <p style={s.body}>
            Traditional agencies charge for the size of their team, whether you need all of it or not.
            RAMDESIGNWORKS charges for the platform — a monthly license tied directly to what you use,
            with revenue share structures that keep our incentives aligned with yours.
          </p>
          <p style={s.body}>
            <span style={s.strong}>Month-to-month licensing.</span> No long-term contracts. No setup
            surprise bills. Infrastructure costs passed through at actual cost. When you grow, we grow.
            When you need less, you pay less. That's how this should work.
          </p>
          <p style={s.body}>
            And because our team operates on AI-native infrastructure, the bandwidth available to your
            project does not depend on how many humans we can staff in a given week. We scale with
            the work, not the headcount.
          </p>
        </div>

        {/* Philosophy — brief */}
        <div style={s.section}>
          <p style={s.sectionLabel}>How We Think</p>
          <h2 style={s.sectionTitle}>We build what you need to run — then get out of the way</h2>
          <p style={s.body}>
            The goal is never dependency. Every platform we deliver is designed so you can operate it
            with the staff you already have. We do not make ourselves indispensable by making systems
            complicated. We make ourselves valuable by making systems that work.
          </p>
          <p style={s.body}>
            If you can describe what you need, we can build it. If you are not sure what you need,
            we can help you figure it out — and we will tell you if the answer is simpler than you think.
          </p>
        </div>

        {/* CTA */}
        <div style={s.cta}>
          <div style={s.ctaTitle}>Ready to see what this looks like for your business?</div>
          <div style={s.ctaBody}>
            Tell us what you're running, what's not working, and what you'd build if it were easy.
            We'll take it from there.
          </div>
          <Link to="/start-a-project" style={s.ctaBtn}>
            &gt;_ Start a Project
          </Link>
        </div>

      </div>

      <footer style={s.footer}>
        &copy; {new Date().getFullYear()} RAMDESIGNWORKS &mdash; AI-Powered Professional Services &mdash; All Rights Reserved
      </footer>

    </div>
  );
}
